import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { scroller } from 'react-scroll';
import { brandActions } from '../../../store/modules/brand';
import { ACCOUNT_SELLER_STATUS } from '../../../constants/service/app/accountSellerConstants';
import { asyncSimpleModal } from '../../../lib/common';
import { BRAND_DETAIL_PAGE_PANEL_NAME, BRAND_TYPE } from '../../../constants/service/app/brandConstants';
import { BALANCE_PERIOD_TYPE } from '../../../constants/service/app/balanceConstants';
import { CONFIRM_TAG_LIST_TYPE, TAG_GROUP_KIND } from '../../../constants/service/app/tagConstants';
import { useEdit } from '../../common/useEdit';
import { useQuery } from '../../common/useQuery';

export const useBrandDetailInfo = (storeId, form) => {
  const query = useQuery();
  const tab = query.get('tabKey');
  const dispatch = useDispatch();

  const { isEditable, edit, cancelEdit } = useEdit(form);

  //편집 취소
  const onCancelEdit = () => {
    cancelEdit();
    fetchBrandDetailInfo();
  };

  useEffect(() => {
    if (tab === '0') {
      onCancelEdit();
    }
  }, [tab]);

  // redux store에서 브랜드 정보 조회
  const brandDetailInfo = useSelector((state) => state.brand.brandDetail.info);

  //redux store에서 브랜드 스토어 태그 목록 조회
  const storeTagGroupList = useSelector((state) => state.brand.storeTagGroupList);

  //브랜드 정보 불러오기
  const fetchBrandDetailInfo = useCallback(
    () => dispatch(brandActions.getBrandDetailInfo({ storeId })),
    [storeId, dispatch],
  );

  // 마운트시에 브랜드 정보 불러오기
  useEffect(() => {
    fetchBrandDetailInfo();
  }, [fetchBrandDetailInfo]);

  const { statusInfo, sellerInfo, brandInfo, businessInfo, balanceInfo, snsInfoRows } = brandDetailInfo;

  /**
   * @name permitRegistrationStoreTag
   * @description [브랜드 상세] 브랜드 태그 승인하기
   * @param {object} option
   *
   * @returns void
   * */
  const permitRegistrationStoreTag = (option) => {
    dispatch(brandActions.permitRegistrationStoreTag({ storeId, ...option, storeTagList: option.tagList }));
  };

  /**
   * @name rejectRegistrationStoreTag
   * @description [브랜드 상세] 브랜드 태그 거절하기
   * @param {object} data
   *
   * @returns void
   * */
  const rejectRegistrationStoreTag = (data) => {
    dispatch(
      brandActions.toggleModal({ name: 'rejectRegistrationStoreTagModal', value: true, data: { storeId, ...data } }),
    );
  };

  /**
   * @name permitBrand
   * @description [브랜드 상세] 브랜드 승인하기
   * @param {string} storeId
   *
   * @returns void
   * */
  const permitBrand = async (storeId) => {
    const confirmResult = await asyncSimpleModal({
      type: 'confirm',
      title: '판매점 입점 승인',
      message: '해당 판매점을 승인합니다. 계속 진행하시겠습니까?',
    });

    if (!confirmResult) return;

    /**
     *  정산주기 업데이트 리스트
     */
    let balancePeriodInfoList = balanceInfo.balancePeriodInfoList.reduce((acc, item) => {
      acc.push({
        ...item,
        balanceDay: item.balancePeriodType === BALANCE_PERIOD_TYPE.DAILY ? null : item.balanceDay,
        balancePeriod: item.balancePeriodType === BALANCE_PERIOD_TYPE.DAILY ? null : item.balancePeriod,
      });

      return acc;
    }, []);

    /**
     *  수수료 업데이트 리스트
     */
    let feeUpdateList = [...balanceInfo.brandFeeInfoList];
    balanceInfo.enabledFulfillment && feeUpdateList.push(...balanceInfo.amondzFeeInfoList);
    feeUpdateList = feeUpdateList.map((item) => ({
      ...item,
      isPgFee: Number(item.isPgFee),
    }));

    dispatch(
      brandActions.permitBrand({
        storeId,
        balancePeriodInfoList,
        feeUpdateList,
      }),
    );
  };

  /**
   * @name onSubmitEditDetailInfo
   * @description [브랜드 상세] 브랜드 편집요청
   *
   * @returns void
   * */
  const onSubmitEditDetailInfo = () => {
    form.validateFieldsAndScroll({ scroll: { offsetTop: 150, offsetBottom: 150 } }, async (err) => {
      if (err) return;

      /**
       * 브랜드가 티어에 등록되어 있으면 입점상태를 비활성화 시킬 수 없다.
       */
      if (brandInfo.isHaveTierBrand && statusInfo.status === ACCOUNT_SELLER_STATUS.INACTIVE) {
        await asyncSimpleModal({
          type: 'error',
          title: '판매점 정보 편집',
          message: '티어를 등록한 브랜드는 비활성화 시킬 수 없습니다.',
        });
        return;
      }

      /**
       * 브랜드를 노출관리하고 있으면 입점상태를 비활성화 시킬 수 없다.
       */
      if (brandInfo.isRankBrand && statusInfo.status === ACCOUNT_SELLER_STATUS.INACTIVE) {
        await asyncSimpleModal({
          type: 'error',
          title: '판매점 정보 편집',
          message: '노출중인 브랜드는 비활성화 시킬 수 없습니다.',
        });
        return;
      }

      /**
       * 앱에 노출 중이면 메인, 서브 이미지를 등록해야한다.
       */
      if (brandInfo.isExposureBrand && (!brandInfo.mainImg.length || !brandInfo.subImg.length)) {
        await asyncSimpleModal({
          type: 'error',
          title: '판매점 정보 편집',
          message: '노출중인 브랜드는 메인, 서브 이미지를 등록해야합니다.',
        });
        return;
      }

      /**
       *  사업자 등록번호 이미지 체크
       */
      if (businessInfo.businessLicenseImg.length < 1) {
        scroller.scrollTo(BRAND_DETAIL_PAGE_PANEL_NAME.BUSINESS);
        return;
      }

      /**
       *  통신판매업 신고 이미지 체크
       */
      if (businessInfo.onlineSalesRegImg.length < 1) {
        scroller.scrollTo(BRAND_DETAIL_PAGE_PANEL_NAME.BUSINESS);
        return;
      }

      /**
       *  통장사본 신고 이미지 체크
       */
      if (balanceInfo.bankBookCopyImg.length < 1) {
        scroller.scrollTo(BRAND_DETAIL_PAGE_PANEL_NAME.BALANCE);
        return;
      }

      /**
       *  정산주기 업데이트 리스트
       */
      let balancePeriodInfoList = balanceInfo.balancePeriodInfoList.reduce((acc, item) => {
        acc.push({
          ...item,
          balanceDay: item.balancePeriodType === BALANCE_PERIOD_TYPE.DAILY ? null : item.balanceDay,
          balancePeriod: item.balancePeriodType === BALANCE_PERIOD_TYPE.DAILY ? null : item.balancePeriod,
        });

        return acc;
      }, []);

      /**
       *  수수료 업데이트 리스트
       */
      let feeUpdateList = [...balanceInfo.brandFeeInfoList];
      balanceInfo.enabledFulfillment && feeUpdateList.push(...balanceInfo.amondzFeeInfoList);
      feeUpdateList = feeUpdateList.map((item) => ({
        ...item,
        isPgFee: Number(item.isPgFee),
      }));
      dispatch(
        brandActions.editBrand({
          /**
           *  판매점 아이디
           */
          storeId,

          /**
           *  담당자 정보
           */
          sellerInfo: {
            email: sellerInfo.email,
            name: sellerInfo.name,
            phone: sellerInfo.phone,
            extraPhone: sellerInfo.extraPhone,
          },

          /**
           *  판매점 정보
           */
          storeInfo: {
            status: statusInfo.status,
            businessType: businessInfo.businessType,
            storeName: brandInfo.storeName,
            storeEngName: brandInfo.storeEngName ? brandInfo.storeEngName : null,
            companyName: businessInfo.companyName,
            ceoName: businessInfo.ceoName,
            businessLicenseNumber: businessInfo.businessLicenseNumber,
            onlineSalesRegNumber: businessInfo.onlineSalesRegNumber,
            phone: brandInfo.storePhone,
            bankId: balanceInfo.bankId,
            accountHolder: balanceInfo.accountHolder,
            accountNumber: balanceInfo.accountNumber,
            homepageUrl: brandInfo.homepageUrl ? brandInfo.homepageUrl : null,
            naverStoreFarmUrl: brandInfo.naverStoreFarmUrl ? brandInfo.naverStoreFarmUrl : null,
            email: balanceInfo.storeEmail ? balanceInfo.storeEmail : null,
            memo: brandInfo.memo,
          },

          /**
           *  판매점 주소 정보
           */
          storeAddressInfo: {
            city: brandInfo.address.city,
            line1: brandInfo.address.line1,
            line2: brandInfo.address.line2 ? brandInfo.address.line2 : null,
            postCode: brandInfo.address.postCode,
          },

          /**
           *  이미지 정보
           */
          storeAttachmentInfo: {
            mainImg: brandInfo.mainImg,
            subImg: brandInfo.subImg,
            businessLicenseImg: businessInfo.businessLicenseImg,
            onlineSalesRegImg: businessInfo.onlineSalesRegImg,
            bankBookCopyImg: balanceInfo.bankBookCopyImg,
          },

          /**
           *  정산 정보
           */
          storeBalanceInfo: {
            balancePeriodInfoList,
            feeUpdateList,
          },

          /**
           *  Sns 정보
           */
          snsInfoRows: snsInfoRows
            /**
             *  인스타, 페북, 블로그 외에 추가된 sns는 배열의 index 별로 no 값을 준다
             */
            .map((item, index) => {
              return {
                ...item,
                no: index > 2 ? index + 1 : item.no,
              };
            })
            /**
             *  typeName, url 이 없으면 유효하지 않은 데이터로 판단해서 제거하고
             */
            .filter((item) => item.typeName && item.url),

          /**
           *  브랜드 태그
           */
          storeTagInfo: {
            /**
             *  브랜드 컨셉 태그
             */
            [TAG_GROUP_KIND.CONCEPT]: brandInfo[TAG_GROUP_KIND.CONCEPT][CONFIRM_TAG_LIST_TYPE.ACTIVE],
          },
        }),
      );
    });
  };

  return {
    permitRegistrationStoreTag,
    rejectRegistrationStoreTag,
    permitBrand,
    onSubmitEditDetailInfo,
    brandDetailInfo,
    storeTagGroupList,
    isEditable,
    edit,
    onCancelEdit,
  };
};
